import { FunctionComponent, MouseEventHandler, useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import useContainerDimensions from "../hooks/useContainerDimensions";
import useDebounce from "../hooks/useDebounce";
import { angleMaxValueSelector, angleMinValueSelector, angleStepSelector, selectedAngleState } from "../state/state";
import '../style/angleGui.css';

/*
To calculate the required height, three nozzle-positions are considered:
- 90° upward
- 90° downward
- horizontal (pointing right)

To calculate it, three values are required
- The height of the monitor-image
- The height that the nozzle is overflowing the monitor if pointing upward, measured from the monitor's rotation axis
  - Named "nozzleOverflowTop"
  - Calculation: nozzle-width minus nozzle-offset-left minus monitor-offset-top
- The height that the nozzle is overflowing the monitor if pointing downward, measured from the monitor's rotation axis
  - Named "nozzleOverflowBottom"
  - Calculation: nozzle-width minus nozzle-offset-left minus monitor-offset-bottom

The required height is calculated in the folowing way:
- monitor-height plus Math.max(nozzleOverflowTop, 0) plus Math.max(nozzleOverflowBottom, 0)
- Math.max is used to cover the case that the nozzle is shorter than the distance between monitor's rotation axis and monitor height,
  otherwise the overflow would be negative.
- As a last step, the required height is raised by 10%, because otherwise corners are cut at certain angles.
*/
const getRequiredHeight = (
  {
    monitorHeight,
    nozzleWidth,
    monitorOffsetTop,
    monitorOffsetBottom,
    nozzleOffsetLeft,
  }: {
    monitorHeight: number,
    nozzleWidth: number,
    monitorOffsetTop: number,
    monitorOffsetBottom: number,
    nozzleOffsetLeft: number,
  }
) => {
  const nozzleOverflowTop = nozzleWidth - monitorOffsetTop - nozzleOffsetLeft;
  const nozzleOverflowBottom = nozzleWidth - monitorOffsetBottom - nozzleOffsetLeft;

  return (monitorHeight + Math.max(nozzleOverflowTop, 0) + Math.max(nozzleOverflowBottom, 0)) * 1.1;
}

/*
Calculation of the maximal required width of monitor and nozzle at horizontal position:
(monitor-width minus monitor-offset-right) plus (nozzle-width minus nozzle-offset-left)
As a last step, the required width is raised by 10%, because otherwise corners are cut at certain angles.
*/
const getRequiredWidth = (
  {
    monitorWidth,
    monitorOffsetRight,
    nozzleWidth,
    nozzleOffsetLeft,
  }: {
    monitorWidth: number,
    monitorOffsetRight: number,
    nozzleWidth: number,
    nozzleOffsetLeft: number,
  }
) => {
  return (monitorWidth - monitorOffsetRight + nozzleWidth - nozzleOffsetLeft) * 1.1;
}

const roundInSteps = (value: number, stepSize: number) => {
  return Math.ceil(value / stepSize) * stepSize;
}

type ImageWithPivot = {
  url: string,
  width: number,
  height: number,
  pivotTop: number,
  pivotLeft: number,
}

type AngleGuiProps = {
  monitorImage: ImageWithPivot,
  nozzleImage: ImageWithPivot,
  displayMonitorImageAboveNozzle: boolean,
}

const AngleGui: FunctionComponent<AngleGuiProps> = ({
  monitorImage,
  nozzleImage,
  displayMonitorImageAboveNozzle,
}) => {

  const containerRef = useRef<HTMLDivElement>(null);
  const angleSelectorRef = useRef<HTMLDivElement>(null);
  const [mouseIsDown, setMouseIsDown] = useState(false);

  useEffect(
    () => {
      // mouse-up must be handled via window, otherwise detecting mouse-up outside of component will not work.
      const handleMouseUp = () => { setMouseIsDown(false) };
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mouseUp', handleMouseUp);
      }
    },
    [],
  )

  const angleMinValue = useRecoilValue(angleMinValueSelector);
  const angleMaxValue = useRecoilValue(angleMaxValueSelector);
  const angleStep = useRecoilValue(angleStepSelector);
  const [selectedAngle, setSelectedAngle] = useRecoilState(selectedAngleState);

  const [angleInput, setAngleInput] = useState(selectedAngle);
  const debouncedAngleInput = useDebounce(angleInput, 10);
  useEffect(
    () => {
      if (selectedAngle !== debouncedAngleInput) {
        setSelectedAngle(debouncedAngleInput);
      }
    },
    [debouncedAngleInput]
  )

  const scaleWidth = 25;
  const scalePadding = 100;

  const requiredWidth = getRequiredWidth({
    monitorWidth: monitorImage.width,
    monitorOffsetRight: monitorImage.width - monitorImage.pivotLeft,
    nozzleWidth: nozzleImage.width,
    nozzleOffsetLeft: nozzleImage.pivotLeft,
  }) + scaleWidth + scalePadding;

  const requiredHeight = getRequiredHeight({
    monitorHeight: monitorImage.height,
    monitorOffsetTop: monitorImage.pivotTop,
    monitorOffsetBottom: monitorImage.height - monitorImage.pivotTop,
    nozzleWidth: nozzleImage.width,
    nozzleOffsetLeft: nozzleImage.pivotLeft,
  }) + (scaleWidth * 2) + (scalePadding * 2);

  const { width: availableWidth, height: availableHeight } = useContainerDimensions(containerRef);
  const sizeFactor = Math.min(availableWidth / requiredWidth, availableHeight / requiredHeight);

  const angles = [];
  for (let angle = angleMinValue; angle <= angleMaxValue; angle += angleStep) {
    angles.push(angle);
  }

  const centerPointLeft = monitorImage.pivotLeft * sizeFactor;
  const centerPointBottom = Math.floor(availableHeight / 2);

  const angleScaleRadius = Math.min(
    availableWidth - centerPointLeft,
    availableHeight - centerPointBottom,
    availableHeight - (availableHeight - centerPointBottom),
  );

  const handleAngleUpdatingMouseEvent: MouseEventHandler<HTMLDivElement> = (e) => {
    const angleSelectorRect = angleSelectorRef.current?.getBoundingClientRect();
    const angleSelectorLeft = angleSelectorRect?.left ?? 0;
    const angleSelectorTop = angleSelectorRect?.top ?? 0;

    const clickRelativeToViewportLeft = e.clientX;
    const clickRelativeToViewportTop = e.clientY;

    const clickRelativeToAngleSelectorLeft = clickRelativeToViewportLeft - angleSelectorLeft;
    const clickRelativeToAngleSelectorTop = clickRelativeToViewportTop - angleSelectorTop;

    const pivotPointRelativeToSelectorLeft = 0;
    const pivotPointRelativeToSelectorTop = availableHeight - (availableHeight - centerPointBottom);

    const angleInDegrees = Math.atan2(
      clickRelativeToAngleSelectorTop - pivotPointRelativeToSelectorTop,
      clickRelativeToAngleSelectorLeft - pivotPointRelativeToSelectorLeft
    ) * (-1) * 180 / Math.PI;

    const angleRounded = roundInSteps(
      Math.max(Math.min(angleInDegrees, angleMaxValue), angleMinValue),
      angleStep
    );

    setAngleInput(angleRounded);
  }

  return (

    <div className="anglegui-container" ref={containerRef}>
      <div
        className="angluegui-nozzle"
        style={{
          position: 'absolute',
          top: Math.floor((availableHeight / 2) - (nozzleImage.pivotTop * sizeFactor)),
          left: Math.floor(centerPointLeft - (nozzleImage.pivotLeft * sizeFactor)),
          width: Math.floor(nozzleImage.width * sizeFactor),
          height: Math.floor(nozzleImage.height * sizeFactor),
          backgroundImage: `url(${nozzleImage.url})`,
          backgroundSize: '100%',
          transformOrigin: `${Math.floor(nozzleImage.pivotLeft * sizeFactor)}px ${Math.floor(nozzleImage.pivotTop * sizeFactor)}px`,
          transform: `rotate(${selectedAngle * (-1)}deg)`,
          zIndex: displayMonitorImageAboveNozzle ? 1 : 2,
        }}
      />
      <div
        className="angluegui-monitor"
        style={{
          position: 'absolute',
          top: Math.floor((availableHeight / 2) - (monitorImage.pivotTop * sizeFactor)),
          left: centerPointLeft - (monitorImage.pivotLeft * sizeFactor),
          width: Math.floor(monitorImage.width * sizeFactor),
          height: Math.floor(monitorImage.height * sizeFactor),
          backgroundImage: `url(${monitorImage.url})`,
          backgroundSize: '100%',
          zIndex: displayMonitorImageAboveNozzle ? 2 : 1,
        }}
      />
      <div
        ref={angleSelectorRef}
        className="angluegui-angleselector"
        style={{
          left: centerPointLeft,
        }}
        onMouseDown={e => {
          handleAngleUpdatingMouseEvent(e);
          setMouseIsDown(true);
        }}
        onMouseMove={e => {
          if (mouseIsDown) {
            handleAngleUpdatingMouseEvent(e);
          }
        }}
      >
        {angles.map(
          (angle, i) => {
            const isSelectedAngleStep = angle === selectedAngle;
            const isMainStep = !isSelectedAngleStep && i % 4 === 2;

            const width = angle === selectedAngle ? `${scaleWidth * 2}px` : isMainStep ? `${scaleWidth}px` : `${scaleWidth / 2}px`;
            const arrowPointLength = 20;
            const arrowWidth = 2;

            return (
              <div
                key={i}
                className={`anglegui-angleselector-element anglegui-angleselector-element--${isSelectedAngleStep ? 'selected-step'
                  : isMainStep ? 'main-step'
                    : 'sub-step'
                  }`}
                style={{
                  bottom: centerPointBottom,
                  width: `${angleScaleRadius}px`,
                  transform: `rotate(${angle * (-1)}deg)`,
                }}
              >
                <div
                  className='anglegui-angleselector-element-child'
                  style={{
                    height: isSelectedAngleStep ? `${arrowWidth}px` : '1px',
                    right: isSelectedAngleStep ? `${arrowPointLength * (-1)}px` : '0',
                    width: width,
                  }}
                >
                  {isSelectedAngleStep && (
                    <div style={{
                      position: 'relative',
                      left: isSelectedAngleStep ? `calc(100% - ${Math.floor(arrowPointLength / 2)}px)` : '100%',
                      top: `-${Math.floor((arrowPointLength * 0.4) - (arrowWidth / 2))}px`,
                      width: 0,
                      height: 0,
                      borderTop: `${arrowPointLength * 0.4}px solid transparent`,
                      borderBottom: `${arrowPointLength * 0.4}px solid transparent`,
                      borderLeft: `${arrowPointLength}px solid gray`,
                    }} />
                  )}
                </div>
              </div>
            )
          }
        )}
      </div>
    </div>
  );
}

export default AngleGui;
